import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs, addDoc, updateDoc, doc, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { Button, Table, Modal, Loader, Text, Space, Divider, MediaQuery, Card, Group, Alert, ActionIcon, Badge, TextInput, Menu, Tooltip, Affix, SegmentedControl } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Plus, Edit, Trash, Upload, Download, Files, Check, X, ArrowBackUp, Copy, Qrcode, Copy as CopyIcon, DotsVertical, Wand, Archive } from "tabler-icons-react";
import { IconX } from "@tabler/icons-react";
import { AIFormGeneratorModal } from "./form-templates/ai-form-generator-modal";
import QRCodeLib from "qrcode";
import FormTemplateEditor from "./form-templates/form-template-editor";
import { installModuleApi, updateModuleApi, ModuleInstallMode } from "../hooks/module-installer";
import { fetchModulesFromFirestore, FlexModule } from "../hooks/modules";
import { useDuplicateTemplate, AuthWithClaims } from "../hooks/useDuplicateTemplate";
import { FlexFormTemplate } from "@captiva/shared-types";
import { replaceUndefinedWithNull } from "../components/listings/hooks";
import { ActionMenu, PageHeader } from "../components/shared";


export interface FormTemplatesProps {
  companyId: string;
  auth: AuthWithClaims;
}

const getModuleColor = (moduleName: string | null | undefined): string => {
  if (!moduleName) return "gray";
  const colors = ["blue", "cyan", "grape", "green", "indigo", "orange", "pink", "red", "teal", "violet"];
  let hash = 0;
  for (let i = 0; i < moduleName.length; i++) {
    hash = moduleName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const getModuleName = (template: FlexFormTemplate, availableModules: FlexModule[]): string | null => {
  if ((template as any).moduleMetadata?.originalModuleName) {
    return (template as any).moduleMetadata.originalModuleName;
  }
  if (template.moduleId) {
    const match = availableModules.find(m => m.id === template.moduleId);
    if (match) return match.name;
    return template.moduleId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return null;
};

const getCaptureUrl = (templateId: string): string => {
  return `https://captivaflex.web.app/capture/${templateId}`;
};

export function FormTemplates({ companyId, auth }: FormTemplatesProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [templates, setTemplates] = useState<FlexFormTemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editorTemplate, setEditorTemplate] = useState<Partial<FlexFormTemplate> | null>(null);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [installModalOpen, setInstallModalOpen] = useState<boolean>(false);
  const [aiModalOpen, setAiModalOpen] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [selectedModule, setSelectedModule] = useState<FlexModule | null>(null);
  const [installingModule, setInstallingModule] = useState<boolean>(false);
  const [installResult, setInstallResult] = useState<any>(null);
  const [installError, setInstallError] = useState<string | null>(null);
  const [availableModules, setAvailableModules] = useState<FlexModule[]>([]);
  const [installMode, setInstallMode] = useState<ModuleInstallMode>('clean');
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [shareTemplate, setShareTemplate] = useState<FlexFormTemplate | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<boolean>(false);

  const installingRef = useRef(false);

  const { duplicateTemplate, duplicating } = useDuplicateTemplate(companyId, auth);

  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  useEffect(() => {
    if (editId && templates.length > 0 && !editorOpen) {
      const templateToEdit = templates.find((t) => t.id === editId);
      if (templateToEdit) {
        setEditorTemplate(templateToEdit);
        setEditorOpen(true);
      }
    }
  }, [editId, templates, editorOpen]);
  useEffect(() => {
    if (shareModalOpen && shareTemplate) {
      setTimeout(() => {
        const canvas = document.getElementById("template-qr-canvas") as HTMLCanvasElement | null;
        if (canvas && shareTemplate.id) {
          const captureUrl = getCaptureUrl(shareTemplate.id);
          QRCodeLib.toCanvas(canvas, captureUrl, {
            width: 200,
            margin: 2,
            errorCorrectionLevel: 'H'
          });
        }
      }, 100);
    }
  }, [shareModalOpen, shareTemplate]);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    let q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId));
    if (!showArchived) {
      q = query(q, where("archived", "==", false));
    }
    getDocs(q).then((snapshot) => {
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(list);
      setLoading(false);
    });

    fetchModulesFromFirestore().then((modules) => {
      setAvailableModules(modules);
    }).catch((error) => {
      console.error('Error fetching modules:', error);
      setAvailableModules([]);
    });
  }, [companyId, showArchived, refresh]);

  const openNewModal = () => {
    setEditorTemplate({ fields: [], name: "", description: "", active: true });
    setEditorOpen(true);
  };

  const openEditor = (template: FlexFormTemplate) => {
    setEditorTemplate(template);
    setEditorOpen(true);
  };

  const handleEditorSave = async (updatedTemplate: Partial<FlexFormTemplate>) => {
    setLoading(true);
    try {
      const sanitizedTemplate = replaceUndefinedWithNull(updatedTemplate);
      let savedTemplate = sanitizedTemplate;
      if (sanitizedTemplate.id) {
        await updateDoc(doc(db, "flexFormTemplates", sanitizedTemplate.id), sanitizedTemplate);
      } else {
        const docRef = await addDoc(collection(db, "flexFormTemplates"), {
          ...sanitizedTemplate,
          archived: sanitizedTemplate.archived ?? false,
          companyId,
          createdBy: auth?.claims?.email || "templates",
          createdAt: serverTimestamp(),
          updatedBy: auth?.claims?.email || "unknown",
          updatedAt: serverTimestamp(),
        });
        savedTemplate = { ...sanitizedTemplate, id: docRef.id };
      }
      setEditorOpen(false);
      let q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId));
      if (!showArchived) {
        q = query(q, where("archived", "==", false));
      }
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(list);
      return savedTemplate;
    } catch (error) {
      console.error("Error saving template:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id: string) => {
    await updateDoc(doc(db, "flexFormTemplates", id), { archived: true });
    if (!showArchived) {
      setTemplates(templates.filter((t) => t.id !== id));
    } else {
      setTemplates(templates.map((t) => (t.id === id ? { ...t, archived: true } : t)));
    }

    try {
      const usersQuery = query(collection(db, "users"), where("companyId", "==", companyId));
      const usersSnapshot = await getDocs(usersQuery);

      const updatePromises = usersSnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        const captivaFlex = (userData.selectedTemplates?.captivaFlex as string[]) || [];
        const flexTemplates = (userData.selectedTemplates?.flexForms as string[]) || [];

        const updatedCaptivaFlex = captivaFlex.filter(templateId => templateId !== id);
        const updatedFlexTemplates = flexTemplates.filter(templateId => templateId !== id);

        const updates: Record<string, string[]> = {};
        if (updatedCaptivaFlex.length !== captivaFlex.length) {
          updates["selectedTemplates.captivaFlex"] = updatedCaptivaFlex;
        }
        if (updatedFlexTemplates.length !== flexTemplates.length) {
          updates["selectedTemplates.flexForms"] = updatedFlexTemplates;
        }

        if (Object.keys(updates).length > 0) {
          await updateDoc(doc(db, "users", userDoc.id), updates);
        }
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating user form configurations:", error);
    }
  };

  const handleUnarchive = async (id: string) => {
    await updateDoc(doc(db, "flexFormTemplates", id), { archived: false });
    if (showArchived) {
      setTemplates(templates.filter((t) => t.id !== id));
    } else {
      setTemplates(templates.map((t) => (t.id === id ? { ...t, archived: false } : t)));
    }
  };

  const handleDuplicate = async (template: FlexFormTemplate) => {
    try {
      await duplicateTemplate(template);

      let q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId));
      if (!showArchived) {
        q = query(q, where("archived", "==", false));
      }
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(list);
    } catch (error) {
      console.error("Error duplicating template:", error);
    }
  };

  const openModuleSelectionModal = () => {
    setSelectedModule(null);
    setInstallMode('clean');
    setInstallModalOpen(true);
  };

  const handleInstallModule = async () => {
    if (!selectedModule || installingRef.current) return;
    installingRef.current = true;

    setInstallingModule(true);
    setInstallError(null);
    setInstallResult(null);

    try {
      const userId = auth?.claims?.sub || "unknown";
      const userEmail = auth?.claims?.email || "module-install";

      const result = await installModuleApi(selectedModule.id, companyId, userId, userEmail, { mode: installMode });

      setInstallResult(result);

      let q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId));
      if (!showArchived) {
        q = query(q, where("archived", "==", false));
      }
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(list);
    } catch (error: any) {
      console.error('Module installation failed:', error);
      setInstallError(error.message || 'Installation failed');
    } finally {
      setInstallingModule(false);
      installingRef.current = false;
    }
  };

  const [updateResult, setUpdateResult] = useState<any>(null);

  const handleUpdateModule = async () => {
    if (!selectedModule || installingRef.current) return;
    installingRef.current = true;

    setInstallingModule(true);
    setInstallError(null);
    setUpdateResult(null);

    try {
      const userId = auth?.claims?.sub || "unknown";
      const userEmail = auth?.claims?.email || "module-update";

      const result = await updateModuleApi(selectedModule.id, companyId, userId, userEmail);

      setUpdateResult(result);

      let q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId));
      if (!showArchived) {
        q = query(q, where("archived", "==", false));
      }
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(list);
    } catch (error: any) {
      console.error('Module update failed:', error);
      setInstallError(error.message || 'Update failed');
    } finally {
      setInstallingModule(false);
      installingRef.current = false;
    }
  };

  if (!companyId) return <Text>No company selected.</Text>;

  return (
    <>
      <PageHeader
        title="Form Configurations"
        subtitle="Manage flexible form templates and field definitions"
        action={
          <div style={{ display: "flex", gap: "1em", alignItems: "center", flexWrap: "wrap" }}>
            <TextInput
              placeholder="Search forms"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: "250px" }}
              rightSection={
                searchTerm ? (
                  <ActionIcon
                    size="xs"
                    color="gray"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
            />
            <Tooltip label={showArchived ? "Hide archived" : "Show archived"} withArrow>
              <ActionIcon
                variant={showArchived ? "filled" : "outline"}
                color={showArchived ? "cap-blue" : "gray"}
                size="lg"
                aria-label={showArchived ? "Hide archived" : "Show archived"}
                title={showArchived ? "Hide archived" : "Show archived"}
                onClick={() => setShowArchived(!showArchived)}
              >
                <Archive size={18} />
              </ActionIcon>
            </Tooltip>

            <ActionMenu
              control={
                <Button color="cap-blue" variant="filled" leftIcon={<Plus size={16} />}>
                  New form
                </Button>
              }
              items={[
                {
                  label: "Create Manually",
                  icon: <Plus size={16} />,
                  onClick: openNewModal,
                },
                {
                  label: "Install Module",
                  icon: <Files size={16} />,
                  onClick: openModuleSelectionModal,
                },
                {
                  label: "Generate with AI",
                  icon: <Wand size={16} />,
                  onClick: () => setAiModalOpen(true),
                },
              ]}
            />
          </div>
        }
      />

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', width: '100%' }}>
          <Loader size="lg" color="cap-blue" />
        </div>
      ) : (() => {
        const filteredTemplates = templates.filter((template) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          const name = (template.name || "").toLowerCase();
          const desc = (template.description || "").toLowerCase();
          return name.includes(term) || desc.includes(term);
        });

        if (filteredTemplates.length === 0) {
          return (
            <Text size="sm" color="dimmed" style={{ margin: "1em auto", width: "100%", textAlign: "center" }}>
              No forms match your search.
            </Text>
          );
        }

        return (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Name</th>
                <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                  <th>Description</th>
                </MediaQuery>
                <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                  <th>Active</th>
                </MediaQuery>
                <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                  <th>Module</th>
                </MediaQuery>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template) => (
                <tr
                  key={template.id}
                  style={{
                    color: (template as any).archived ? "gray" : "inherit",
                  }}
                >
                  <td>{template.name}</td>
                  <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                    <td>{template.description}</td>
                  </MediaQuery>
                  <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                    <td>
                      {(template as any).archived ? (
                        <Badge color="orange" variant="light" size="sm" leftSection={<Archive size={12} />}>
                          Archived
                        </Badge>
                      ) : template.active ? (
                        <Badge color="green" variant="light" size="sm" leftSection={<Check size={12} />}>
                          Active
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light" size="sm" leftSection={<X size={12} />}>
                          Inactive
                        </Badge>
                      )}
                    </td>
                  </MediaQuery>
                  <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                    <td>
                      {(() => {
                        const modName = getModuleName(template, availableModules);
                        return modName ? (
                          <Badge color={getModuleColor(modName)} variant="light">
                            {modName}
                          </Badge>
                        ) : "-";
                      })()}
                    </td>
                  </MediaQuery>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                      <ActionMenu
                        control={
                          <ActionIcon
                            variant="outline"
                            color="cap-blue"
                            size="lg"
                            aria-label={`Actions for form ${template.name || template.id}`}
                          >
                            <DotsVertical size={18} />
                          </ActionIcon>
                        }
                        items={[
                          {
                            label: "Edit Form",
                            icon: <Edit size={16} />,
                            onClick: () => openEditor(template),
                          },
                          {
                            label: "Duplicate",
                            icon: <Copy size={16} />,
                            onClick: () => handleDuplicate(template),
                            disabled: duplicating,
                          },
                          template.isPublicCaptureEnabled && {
                            label: "QR Code / Share",
                            icon: <Qrcode size={16} />,
                            onClick: () => {
                              setShareTemplate(template);
                              setShareModalOpen(true);
                            },
                          },
                          (template as any).archived ? {
                            label: "Restore",
                            icon: <Upload size={16} />,
                            onClick: () => handleUnarchive(template.id!),
                          } : {
                            label: "Archive Form",
                            icon: <Trash size={16} />,
                            onClick: () => handleArchive(template.id!),
                            color: "red",
                          }
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        );
      })()}
      <AIFormGeneratorModal
        opened={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        companyId={companyId}
        onSuccess={() => setRefresh((prev) => !prev)}
      />
      {isMobile && (
        <Affix position={{ bottom: 24, right: 24 }} zIndex={99}>
          <ActionMenu
            control={
              <ActionIcon
                color="cap-blue"
                variant="filled"
                radius="xl"
                size={56}
                aria-label="Create new form"
                sx={{
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  "&:active": { transform: "scale(0.95)" },
                }}
              >
                <Plus size={28} />
              </ActionIcon>
            }
            items={[
              {
                label: "Create Manually",
                icon: <Plus size={16} />,
                onClick: openNewModal,
              },
              {
                label: "Install Module",
                icon: <Files size={16} />,
                onClick: openModuleSelectionModal,
              },
              {
                label: "Generate with AI",
                icon: <Wand size={16} />,
                onClick: () => setAiModalOpen(true),
              },
            ]}
          />
        </Affix>
      )}
      <Modal
        opened={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          if (searchParams.has("editId")) {
            searchParams.delete("editId");
            setSearchParams(searchParams, { replace: true });
          }
        }}
        title={editorTemplate?.id ? "Edit form" : "New form"}
        size={isMobile ? "100%" : "xl"}
        padding={isMobile ? 0 : "lg"}
        styles={isMobile ? {
          modal: {
            height: '100vh',
            maxHeight: '100vh',
            width: '100vw',
            maxWidth: '100vw',
            margin: 0,
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
          },
          inner: {
            padding: 0,
            overflow: 'hidden',
          },
          header: {
            padding: '12px 16px',
            margin: 0,
            borderBottom: '1px solid #e9ecef',
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: 0,
          },
        } : undefined}
        {...({ fullScreen: isMobile } as any)}
      >
        {editorOpen && (
          <FormTemplateEditor
            template={editorTemplate || { fields: [], name: "", description: "", active: true }}
            onSave={handleEditorSave}
            onCancel={() => {
              setEditorOpen(false);
              if (searchParams.has("editId")) {
                searchParams.delete("editId");
                setSearchParams(searchParams, { replace: true });
              }
            }}
            allTemplates={templates}
            companyId={companyId}
          />
        )}
      </Modal>
      <Modal
        opened={installModalOpen}
        onClose={() => { setInstallModalOpen(false); setSelectedModule(null); setInstallResult(null); setUpdateResult(null); setInstallError(null); setInstallMode('clean'); }}
        title={selectedModule ? `Install ${selectedModule.name}` : "Select Module to Install"}
        size="xl"
      >
        {selectedModule ? (
          <>
            {installError && (
              <Alert icon={<X size={16} />} title="Installation Failed" color="red" mb="md">
                {installError}
              </Alert>
            )}

            {installResult && (
              <Alert icon={<Check size={16} />} title="Installation Successful!" color="green" mb="md">
                <Text size="sm">
                  Successfully installed {selectedModule.name} module.
                  <br />
                  • {installResult.installedComponents.templates.length} templates created
                  <br />
                  • {installResult.installedComponents.reports.length} reports created
                  <br />
                  • {installResult.installedComponents.sampleOptionsSets.length} sample options sets created
                  {installResult.sampleSubmissionsCreated ? (
                    <>
                      <br />
                      • {installResult.sampleSubmissionsCreated} sample submissions seeded
                    </>
                  ) : null}
                </Text>
                {installResult.instructions && (
                  <Text size="sm" mt="xs" color="dimmed">
                    {installResult.instructions}
                  </Text>
                )}
              </Alert>
            )}

            {updateResult && (
              <Alert icon={<Check size={16} />} title="Update Successful!" color="green" mb="md">
                <Text size="sm">
                  Successfully updated {selectedModule.name} module.
                  <br />
                  • {updateResult.updatedComponents.templates?.length || 0} templates updated (fields and conditional display synchronized)
                  <br />
                  • {updateResult.updatedComponents.reports.length} reports updated (post-processing parameters backfilled)
                  <br />
                  • {updateResult.updatedComponents.optionsSets.length} options sets updated
                </Text>
              </Alert>
            )}

            {!installResult && !installError && (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <Text size="sm" weight={600} mb="xs">
                    Installation Mode
                  </Text>
                  <SegmentedControl
                    value={installMode}
                    onChange={(val) => setInstallMode(val as ModuleInstallMode)}
                    data={[
                      { label: "Clean Install", value: "clean" },
                      { label: "Install with Sample Data", value: "sample" },
                    ]}
                    fullWidth
                    color="cap-blue"
                  />
                  <Text size="xs" color="dimmed" mt={6}>
                    {installMode === "clean"
                      ? "Clean Install (Default): Installs templates and reports with empty dynamic option sets ready for production operations."
                      : "Install with Sample Data: Seeds realistic sample submissions and matching options for demonstration and evaluation."}
                  </Text>
                </div>

                <Divider />
                <Space h="md" />
                <Text size="md" weight={500}>Templates </Text>
                {selectedModule.templates?.map((template: any, index: number) => (
                  <Text key={index} size="md" ml="md">
                    • {template.name}
                  </Text>
                ))}
                <Space h="xs" />
                <Text size="md" weight={500}>Options</Text>
                {(selectedModule.optionsSets || selectedModule.sampleOptionsSets)?.map((optionsSet: any, index: number) => (
                  <Text key={index} size="md" ml="md">
                    • {optionsSet.name}
                  </Text>
                ))}
                <Space h="xs" />
                <Text size="md" weight={500}>Reports </Text>
                {selectedModule.reports?.map((report: any, index: number) => (
                  <Text key={index} size="md" ml="md">
                    • {report.name}
                  </Text>
                ))}
                <Space h="md" />
              </>
            )}

            <Group position="right">
              {installResult || updateResult ? (
                <>
                  <Button
                    variant="outline"
                    color="gray"
                    onClick={() => {
                      setSelectedModule(null);
                      setInstallResult(null);
                      setUpdateResult(null);
                      setInstallError(null);
                      setInstallMode('clean');
                    }}
                  >
                    Back to Modules
                  </Button>
                  <Button
                    variant="default"
                    color="gray"
                    onClick={() => {
                      setInstallModalOpen(false);
                      setSelectedModule(null);
                      setInstallResult(null);
                      setUpdateResult(null);
                      setInstallError(null);
                      setInstallMode('clean');
                    }}
                  >
                    Close
                  </Button>
                </>
              ) : (
                <Button
                  variant="default"
                  color="gray"
                  leftIcon={<ArrowBackUp size={16} />}
                  onClick={() => {
                    setSelectedModule(null);
                    setInstallResult(null);
                    setUpdateResult(null);
                    setInstallError(null);
                    setInstallMode('clean');
                  }}
                  disabled={installingModule}
                >
                  Back
                </Button>
              )}
              {!installResult && !updateResult && (() => {
                const installedTemplates = templates.filter(
                  t => t.moduleId === selectedModule.id || (t as any).moduleMetadata?.originalModuleId === selectedModule.id
                );
                const isModuleInstalled = installedTemplates.length > 0;

                // Determine if an update is available by comparing versions
                const installedVersion = (installedTemplates[0] as any)?.moduleMetadata?.version || "1.0.0";
                const availableVersion = selectedModule.version || "1.0.0";
                const isUpdateAvailable = isModuleInstalled && (installedVersion !== availableVersion);

                // Show a disabled button if it is already installed and up-to-date
                if (isModuleInstalled && !isUpdateAvailable) {
                  return (
                    <Button disabled variant="outline" color="gray">
                      Already up to date
                    </Button>
                  );
                }

                return (
                  <Button
                    color={isModuleInstalled ? "teal" : "cap-navy"}
                    leftIcon={isModuleInstalled ? <Check size={16} /> : <Download size={16} />}
                    onClick={isModuleInstalled ? handleUpdateModule : handleInstallModule}
                    loading={installingModule}
                    disabled={installingModule}
                  >
                    {installingModule
                      ? (isModuleInstalled ? 'Updating...' : 'Installing...')
                      : (isModuleInstalled ? 'Update Installation' : 'Install')}
                  </Button>
                );
              })()}
            </Group>
          </>
        ) : (
          <Group spacing="md">
            {availableModules.map((module) => (
              <Card key={module.id} shadow="sm" p="lg" radius="md" withBorder style={{ minWidth: 300 }}>
                <Text weight={500}>{module.name}</Text>
                <Text size="sm" color="dimmed" mt="xs">{module.description}</Text>
                <Button
                  color="cap-blue"
                  variant="filled"
                  leftIcon={<Download size={16} />}
                  mt="md"
                  onClick={() => setSelectedModule(module)}
                >
                  Select Module
                </Button>
              </Card>
            ))}
          </Group>
        )}
      </Modal>

      <Modal
        opened={shareModalOpen}
        onClose={() => { setShareModalOpen(false); setShareTemplate(null); }}
        title={`Online Capture Link & QR Code`}
        size="md"
        centered
      >
        {shareTemplate && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem", padding: "1rem" }}>
            <Text size="sm" color="dimmed" align="center">
              Point a device camera at this QR code or use the link below to submit entries directly from a browser.
            </Text>

            <canvas id="template-qr-canvas" style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "8px", background: "#fff" }} />

            <TextInput
              label="Form Web URL"
              value={getCaptureUrl(shareTemplate.id || '')}
              readOnly
              style={{ width: "100%" }}
              rightSection={
                <ActionIcon
                  aria-label="Copy form URL to clipboard"
                  color={copiedUrl ? "teal" : "gray"}
                  onClick={() => {
                    const url = getCaptureUrl(shareTemplate.id || '');
                    navigator.clipboard.writeText(url);
                    setCopiedUrl(true);
                    setTimeout(() => setCopiedUrl(false), 1500);
                  }}
                >
                  {copiedUrl ? <Check size={16} color="teal" /> : <CopyIcon size={16} />}
                </ActionIcon>
              }
            />

            <Group position="center" style={{ width: "100%" }}>
              <Button
                color="cap-blue"
                variant="outline"
                leftIcon={<Download size={16} />}
                onClick={() => {
                  const canvas = document.getElementById("template-qr-canvas") as HTMLCanvasElement | null;
                  if (canvas) {
                    const pngUrl = canvas.toDataURL("image/png");
                    const downloadLink = document.createElement("a");
                    downloadLink.href = pngUrl;
                    downloadLink.download = `${(shareTemplate.name || "").toLowerCase().replace(/[^a-z0-9]/g, "-")}-qr-code.png`;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                  }
                }}
              >
                Download QR Code
              </Button>
            </Group>
          </div>
        )}
      </Modal>

      <Space h="xl" />
    </>
  );
}

export default FormTemplates;
